import { useCallback, useEffect, useState } from 'react';
import { CasesTable, EvaluationNote, RulesTable } from '../components/quality/CorrectionsView';
import { errorText } from '../lib/assistantView';
import { fmtTime } from '../lib/jobView';
import {
  correctionError,
  latestFullRun,
  needsConfirm,
  runAccuracy,
  runLabel,
  type Correction,
  type CorrectionEvaluation,
  type TextEvalCase,
  type TextEvalRun,
} from '../lib/latinView';
import { hubClient } from '../services/hubClient';

const messageOf = (e: unknown) => (e instanceof Error ? e.message : String(e));
const saveError = (e: unknown) => correctionError(messageOf(e));

/** An all-bots save the hub asked to confirm (P3 gate). */
type Pending = { message: string; evaluation: CorrectionEvaluation | null };

function ConfirmBox({ pending, onConfirm, onCancel, busy }: { pending: Pending; onConfirm: () => void; onCancel: () => void; busy: boolean }) {
  return (
    <div className="rounded border border-amber-300 bg-amber-50 p-2 space-y-1 text-sm">
      <p className="text-amber-800">{pending.message}</p>
      {pending.evaluation && <EvaluationNote evaluation={pending.evaluation} />}
      <button type="button" className="px-2 py-1 rounded bg-amber-600 text-white" disabled={busy} onClick={onConfirm}>
        Save anyway
      </button>{' '}
      <button type="button" className="text-gray-600" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

/**
 * Platform → Text conversion (Mungun/infra text-conversion review, P2): corrections for all bots and the operators'
 * "suggest for all bots" queue. Global admins only (the server enforces). A bot's own correction still wins for that bot.
 */
export function TextConversionAdmin() {
  const [rules, setRules] = useState<Correction[] | null>(null);
  const [queue, setQueue] = useState<Correction[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [r, q] = await Promise.all([hubClient.globalCorrections(), hubClient.correctionSuggestions()]);
      setRules(r);
      setQueue(q);
    } catch (e) {
      setError(errorText(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-6 p-4">
      <div>
        <h1 className="text-xl font-semibold">Text conversion</h1>
        <p className="text-sm text-gray-600">
          Corrections here apply to every bot when a customer's Latin message is converted to Cyrillic. Use them only for
          words written the same way everywhere — a word whose spelling depends on the word before it (like the question
          word <span className="font-mono">u</span> → уу / үү) is better corrected per bot or as 2 words. Changes are live
          within about a minute.
        </p>
      </div>
      {error && <p className="text-sm text-red-700">{error}</p>}

      <section className="space-y-2">
        <h2 className="text-base font-medium">Suggested for all bots</h2>
        {!queue ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : queue.length === 0 ? (
          <p className="text-sm text-gray-500">No suggestions waiting.</p>
        ) : (
          <SuggestionsTable rows={queue} onDone={load} />
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-medium">All-bots corrections</h2>
        <AddGlobal onDone={load} />
        {!rules ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <RulesTable
            rules={rules}
            empty="No all-bots corrections yet."
            onUpdate={(r, body) =>
              hubClient
                .globalCorrectionUpdate(r.id, { cyrillic: body.cyrillic, enabled: body.enabled, confirm: body.confirm })
                .then(async (saved) => (await load(), saved))
            }
            loadChanges={(r) => hubClient.globalCorrectionChanges(r.id)}
          />
        )}
      </section>

      <CheckedConversions />
    </div>
  );
}

/** P3: every checked conversion, starter cases, runs over time and the failures of the latest run. */
function CheckedConversions() {
  const [cases, setCases] = useState<TextEvalCase[] | null>(null);
  const [runs, setRuns] = useState<TextEvalRun[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [seedBot, setSeedBot] = useState({ system: 'chatbot', botId: '' });

  const load = useCallback(async () => {
    try {
      const [c, r] = await Promise.all([hubClient.evalCases(), hubClient.evalRuns(30)]);
      setCases(c);
      setRuns(r);
    } catch (e) {
      setError(errorText(e));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const act = async (fn: () => Promise<string>) => {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      setNote(await fn());
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const byId = new Map((cases ?? []).map((c) => [c.id, c]));
  const counts = (cases ?? []).reduce<Record<string, number>>((m, c) => ({ ...m, [c.source]: (m[c.source] ?? 0) + 1 }), {});
  const latest = runs ? latestFullRun(runs) : null;
  const [showCases, setShowCases] = useState(false);
  return (
    <section className="space-y-2">
      <h2 className="text-base font-medium">Checked conversions</h2>
      <p className="text-sm text-gray-600">
        Messages whose right conversion is known. Every correction save is tested against them; an all-bots change that
        breaks one asks to confirm. Operators add them with <b>Looks right</b> on a bot's Latin text tab.
      </p>
      {error && <p className="text-sm text-red-700">{error}</p>}
      {note && <p className="text-sm text-green-700">{note}</p>}
      {!cases ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <p className="text-sm">
          {cases.length} cases — confirmed {counts.confirmed ?? 0}, starter {counts.seed ?? 0}, from corrections {counts.correction ?? 0}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <button
          type="button"
          disabled={busy}
          className="px-3 py-1 rounded bg-blue-600 text-white disabled:opacity-50"
          onClick={() => void act(async () => `Run done: ${runAccuracy(await hubClient.evalRun())} right`)}
        >
          {busy ? 'Working…' : 'Run now'}
        </button>
        <button
          type="button"
          disabled={busy}
          className="px-3 py-1 rounded border border-gray-300"
          onClick={() => void act(async () => seedText(await hubClient.evalSeed({})))}
        >
          Add known wrong conversions
        </button>
        <span className="text-gray-500">or a bot's intent phrases:</span>
        <select className="border border-gray-300 rounded px-1 py-1" value={seedBot.system} onChange={(e) => setSeedBot({ ...seedBot, system: e.target.value })}>
          <option value="chatbot">v1 (chatbot)</option>
          <option value="chatbot_v2">v2 (chatbot_v2)</option>
        </select>
        <input
          aria-label="Bot id"
          className="border border-gray-300 rounded px-2 py-1 w-56 font-mono"
          placeholder="bot id"
          value={seedBot.botId}
          onChange={(e) => setSeedBot({ ...seedBot, botId: e.target.value.trim() })}
        />
        <button
          type="button"
          disabled={busy || !seedBot.botId}
          className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
          onClick={() => void act(async () => seedText(await hubClient.evalSeed(seedBot)))}
        >
          Add from intents
        </button>
      </div>
      {cases && cases.length > 0 && (
        <button type="button" className="text-sm text-blue-700" onClick={() => setShowCases(!showCases)}>
          {showCases ? 'Hide cases' : `Show all ${cases.length} cases`}
        </button>
      )}
      {showCases && cases && <CasesTable cases={cases} showBot onDelete={(c) => hubClient.evalCaseDelete(c.id).then(load)} />}
      {runs && runs.length > 0 && (
        <table className="w-full text-sm bg-white border border-gray-200 rounded-lg">
          <thead className="text-left text-xs text-gray-500">
            <tr>
              <th className="p-2">When</th>
              <th className="p-2">Cases</th>
              <th className="p-2">Right</th>
              <th className="p-2">Why</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} className="border-t">
                <td className="p-2 text-gray-600">{fmtTime(r.createdAt)}</td>
                <td className="p-2">{r.cases}</td>
                <td className="p-2">{runAccuracy(r)}</td>
                <td className="p-2 text-gray-600">{runLabel(r)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {latest && latest.failed.length > 0 && (
        <div className="space-y-1">
          <h3 className="text-sm font-medium text-gray-700">Wrong in the latest full run</h3>
          <ul className="text-xs space-y-0.5">
            {latest.failed.map((f) => {
              const c = byId.get(f.caseId);
              return (
                <li key={f.caseId} className="font-mono">
                  {c ? `${c.latin} → ${f.got} (expected ${c.expected})` : `${f.got} (case removed)`}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}


const seedText = (r: { added: number; skipped: number }) => `Added ${r.added} cases${r.skipped ? ` (${r.skipped} already there)` : ''}`;

function SuggestionsTable({ rows, onDone }: { rows: Correction[]; onDone: () => Promise<void> }) {
  const [text, setText] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<{ id: string; text: string } | null>(null);
  // the Cyrillic that was checked: "Save anyway" resends exactly that (final review #2)
  const [pending, setPending] = useState<({ id: string; cyrillic: string } & Pending) | null>(null);

  const act = async (r: Correction, approve: boolean, confirmed?: string) => {
    setBusy(r.id);
    setError(null);
    setPending(null);
    const cyrillic = confirmed ?? text[r.id] ?? r.cyrillic;
    try {
      if (approve) await hubClient.correctionApprove(r.id, cyrillic, confirmed !== undefined);
      else await hubClient.correctionReject(r.id);
      await onDone();
    } catch (e) {
      const ask = needsConfirm(messageOf(e));
      if (ask) setPending({ id: r.id, cyrillic, ...ask });
      else setError({ id: r.id, text: saveError(e) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <table className="w-full text-sm bg-white border border-gray-200 rounded-lg">
      <thead className="text-left text-xs text-gray-500">
        <tr>
          <th className="p-2">Latin</th>
          <th className="p-2">Cyrillic</th>
          <th className="p-2">Bot</th>
          <th className="p-2">Suggested by</th>
          <th className="p-2" />
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-t align-top">
            <td className="p-2 font-mono">{r.latin}</td>
            <td className="p-2">
              <input
                aria-label={`Cyrillic for ${r.latin}`}
                className="border border-gray-300 rounded px-1 py-0.5 font-mono w-40"
                value={text[r.id] ?? r.cyrillic}
                maxLength={120}
                onChange={(e) => {
                  setText({ ...text, [r.id]: e.target.value });
                  if (pending?.id === r.id) setPending(null);   // a changed text must be checked again
                }}
              />
              {r.note && <p className="text-xs text-gray-500 mt-1">{r.note}</p>}
              {error?.id === r.id && <p className="text-xs text-red-700 mt-1">{error.text}</p>}
              {pending?.id === r.id && (
                <ConfirmBox pending={pending} busy={busy === r.id} onConfirm={() => void act(r, true, pending.cyrillic)} onCancel={() => setPending(null)} />
              )}
            </td>
            <td className="p-2 text-gray-600">
              {r.system} / {r.botId}
            </td>
            <td className="p-2 text-gray-600">{r.createdBy ?? '—'}</td>
            <td className="p-2 text-right space-x-2 whitespace-nowrap">
              <button type="button" className="text-blue-700" disabled={busy === r.id} onClick={() => void act(r, true)}>
                Approve
              </button>
              <button type="button" className="text-gray-600" disabled={busy === r.id} onClick={() => void act(r, false)}>
                Reject
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function AddGlobal({ onDone }: { onDone: () => Promise<void> }) {
  const [form, setForm] = useState({ latin: '', cyrillic: '', note: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // the body that was checked: "Save anyway" resends exactly that (final review #2)
  const [pending, setPending] = useState<(Pending & { body: { latin: string; cyrillic: string; note?: string } }) | null>(null);
  const [result, setResult] = useState<{ evaluation?: CorrectionEvaluation | null } | null>(null);

  const submit = async (confirmed?: { latin: string; cyrillic: string; note?: string }) => {
    setBusy(true);
    setError(null);
    setPending(null);
    setResult(null);
    const body = confirmed ?? { latin: form.latin, cyrillic: form.cyrillic, note: form.note || undefined };
    try {
      const saved = await hubClient.globalCorrectionCreate({ ...body, confirm: confirmed !== undefined });
      setForm({ latin: '', cyrillic: '', note: '' });
      setResult(saved);
      await onDone();
    } catch (e) {
      const ask = needsConfirm(messageOf(e));
      if (ask) setPending({ ...ask, body });
      else setError(saveError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <input
        aria-label="Latin"
        className="border border-gray-300 rounded px-2 py-1 font-mono w-36"
        placeholder="yaj"
        value={form.latin}
        maxLength={80}
        onChange={(e) => setForm({ ...form, latin: e.target.value })}
      />
      →
      <input
        aria-label="Cyrillic"
        className="border border-gray-300 rounded px-2 py-1 font-mono w-40"
        placeholder="яаж"
        value={form.cyrillic}
        maxLength={120}
        onChange={(e) => setForm({ ...form, cyrillic: e.target.value })}
      />
      <input
        aria-label="Note"
        className="border border-gray-300 rounded px-2 py-1 w-48"
        placeholder="Note (optional)"
        value={form.note}
        maxLength={200}
        onChange={(e) => setForm({ ...form, note: e.target.value })}
      />
      <button
        type="button"
        disabled={busy || !form.latin.trim() || !form.cyrillic.trim()}
        onClick={() => void submit()}
        className="px-3 py-1 rounded bg-blue-600 text-white disabled:opacity-50"
      >
        {busy ? 'Adding…' : 'Add for all bots'}
      </button>
      {error && <span className="text-red-700">{error}</span>}
      {pending && (
        <div className="w-full">
          <ConfirmBox pending={pending} busy={busy} onConfirm={() => void submit(pending.body)} onCancel={() => setPending(null)} />
        </div>
      )}
      {result && result.evaluation !== undefined && (
        <div className="w-full">
          <EvaluationNote evaluation={result.evaluation} />
        </div>
      )}
    </div>
  );
}
